/**
 * SignaturePad
 *
 * Modal capture surface that produces a PNG data URL from either a typed
 * name (rendered to canvas in a script-like font) or a hand-drawn stroke.
 *
 * Both tabs ultimately funnel through the same canvas → toDataURL pipeline
 * so the rest of the signing flow (and the sealing pipeline in Phase 6)
 * stays one-shape: every signature is a PNG.
 *
 * Accessibility: keyboard-only signers can finish the flow via the Type
 * tab. The Draw tab announces the "clear" / "save" affordances and supports
 * mouse + touch input. We intentionally don't try to make stroke-drawing
 * keyboard-driven — the typed alternative is sufficient.
 */

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SignaturePadProps {
  open: boolean;
  initialMode?: 'type' | 'draw';
  defaultName?: string;
  /**
   * `'signature'` (default) — the pad collects a full signature.
   * `'initial'` — the pad collects initials only. Changes the dialog
   * heading + aria-label so it's clear what's being captured. Callers
   * should also pass `defaultName` set to the initials they want
   * pre-filled (the pad itself doesn't derive them — the recipient's
   * name lives in the caller).
   */
  purpose?: 'signature' | 'initial';
  onCancel: () => void;
  onSave: (dataUrl: string) => void;
}

/**
 * Pad canvas dimensions. Two profiles so the rendered output matches the
 * typical field rect aspect ratio on the sealed PDF — without this, the
 * canvas image letterboxes inside the field box and the text scales down
 * to nothing.
 *
 * Signature: ~4:1, matches a wide horizontal signature field.
 * Initial:   ~1.5:1, matches a short squarish initial box.
 *
 * Heights are kept identical so the dialog (and the draw tab) stay the
 * same size visually regardless of purpose.
 */
const SIGNATURE_CANVAS_WIDTH = 640;
const INITIAL_CANVAS_WIDTH = 240;
const CANVAS_HEIGHT = 160;

const SIGNATURE_FONT_FAMILY = '"Brush Script MT", "Lucida Handwriting", cursive';
/**
 * Target font height as a fraction of canvas height. Was effectively 0.3
 * (48px on 160px) before, which produced tiny text once the canvas was
 * fit into a small field rect on the sealed PDF — the signature output
 * was unreadable on the final document. 0.7 makes the text actually fill
 * the pad output, and the renderer auto-shrinks if a long name exceeds
 * the canvas width.
 */
const TYPED_FONT_HEIGHT_FRACTION = 0.7;
const TYPED_FONT_MIN_PX = 24;
/** Reserve a 10% horizontal margin on the typed canvas so the text never
 * touches the canvas edge — preserves a small visual gutter when the
 * canvas is drawn into the field box on the sealed PDF. */
const TYPED_TEXT_WIDTH_FRACTION = 0.9;

export function SignaturePad({
  open,
  initialMode = 'type',
  defaultName = '',
  purpose = 'signature',
  onCancel,
  onSave,
}: SignaturePadProps) {
  const heading = purpose === 'initial' ? 'Add your initials' : 'Add your signature';
  // Canvas width tracks the purpose so the captured image lands in the
  // field box on the sealed PDF without aspect-ratio letterboxing.
  const canvasWidth = purpose === 'initial' ? INITIAL_CANVAS_WIDTH : SIGNATURE_CANVAS_WIDTH;
  const [mode, setMode] = useState<'type' | 'draw'>(initialMode);
  const [typedName, setTypedName] = useState(defaultName);
  const drawCanvasRef = useRef<HTMLCanvasElement>(null);
  const [drawHasInk, setDrawHasInk] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setTypedName(defaultName);
      setDrawHasInk(false);
    }
  }, [open, initialMode, defaultName]);

  // Wire up the draw canvas only when its tab is visible to avoid binding
  // event listeners on a hidden node and to reset state when re-opening.
  useEffect(() => {
    if (!open || mode !== 'draw') return;
    const canvas = drawCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = CANVAS_HEIGHT * dpr;
    // Display size is driven by CSS (w-full + aspect-ratio) so the pad fits
    // inside the dialog on narrow viewports. The backing bitmap stays at
    // canvasWidth × CANVAS_HEIGHT so the captured PNG still matches the
    // sealed-PDF field rect aspect ratio; pointFromEvent below scales the
    // rendered pointer coords back into that drawing space.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // No background fill — the PNG keeps an alpha channel so a signature
    // dropped on a form line in the sealed PDF doesn't blot out the line
    // with an opaque white rectangle. Stroke color carries full alpha; the
    // canvas defaults to transparent.
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#0f172a';

    let drawing = false;
    let last: { x: number; y: number } | null = null;

    const pointFromEvent = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      // Map the pointer's CSS-pixel offset within the rendered canvas back
      // into the drawing coordinate space (canvasWidth × CANVAS_HEIGHT).
      // When the dialog shrinks the canvas via CSS the rendered rect is
      // smaller than the drawing space, so the offset must be scaled — or
      // strokes would land left of and above the cursor.
      const scaleX = rect.width > 0 ? canvasWidth / rect.width : 1;
      const scaleY = rect.height > 0 ? CANVAS_HEIGHT / rect.height : 1;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    };

    const onDown = (e: PointerEvent) => {
      e.preventDefault();
      drawing = true;
      last = pointFromEvent(e);
      canvas.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!drawing || !last) return;
      const next = pointFromEvent(e);
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(next.x, next.y);
      ctx.stroke();
      last = next;
      setDrawHasInk(true);
    };
    const onUp = () => {
      drawing = false;
      last = null;
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointerleave', onUp);
    canvas.addEventListener('pointercancel', onUp);

    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointerleave', onUp);
      canvas.removeEventListener('pointercancel', onUp);
    };
  }, [open, mode, canvasWidth]);

  if (!open) return null;

  function clearDraw() {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // clearRect (not fillRect-white) so the canvas returns to transparent
    // and the next stroke produces an alpha-preserving PNG.
    ctx.clearRect(0, 0, canvasWidth, CANVAS_HEIGHT);
    setDrawHasInk(false);
  }

  function renderTyped(): string | null {
    const trimmed = typedName.trim();
    if (!trimmed) return null;
    const canvas = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = CANVAS_HEIGHT * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // No background fill — produce an alpha PNG so the typed name doesn't
    // mask underlying form lines when stamped on the sealed PDF.
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Auto-fit font: start at the target height fraction and shrink only
    // if the text overflows the canvas width. Cap at TYPED_FONT_MIN_PX so
    // an absurdly long name still draws something rather than truncating
    // to nothing.
    let fontPx = Math.floor(CANVAS_HEIGHT * TYPED_FONT_HEIGHT_FRACTION);
    ctx.font = `${fontPx}px ${SIGNATURE_FONT_FAMILY}`;
    const maxTextWidth = canvasWidth * TYPED_TEXT_WIDTH_FRACTION;
    while (fontPx > TYPED_FONT_MIN_PX && ctx.measureText(trimmed).width > maxTextWidth) {
      fontPx -= 4;
      ctx.font = `${fontPx}px ${SIGNATURE_FONT_FAMILY}`;
    }

    ctx.fillText(trimmed, canvasWidth / 2, CANVAS_HEIGHT / 2);
    return canvas.toDataURL('image/png');
  }

  function handleSave() {
    if (mode === 'type') {
      const url = renderTyped();
      if (url) onSave(url);
      return;
    }
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    if (!drawHasInk) return;
    onSave(canvas.toDataURL('image/png'));
  }

  const canSave = mode === 'type' ? typedName.trim().length > 0 : drawHasInk;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={heading}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-3xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{heading}</h2>
        </div>

        <div className="flex gap-2 border-b">
          <button
            type="button"
            onClick={() => setMode('type')}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${
              mode === 'type'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground'
            }`}
          >
            Type
          </button>
          <button
            type="button"
            onClick={() => setMode('draw')}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${
              mode === 'draw'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground'
            }`}
          >
            Draw
          </button>
        </div>

        {mode === 'type' ? (
          <div className="space-y-3">
            <Label htmlFor="signature-pad-typed-name">
              {purpose === 'initial' ? 'Type your initials' : 'Type your name'}
            </Label>
            <Input
              id="signature-pad-typed-name"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              autoFocus
              maxLength={150}
            />
            <div
              className="border rounded bg-white text-center py-6"
              style={{
                fontFamily: SIGNATURE_FONT_FAMILY,
                fontSize: purpose === 'initial' ? 56 : 44,
                color: '#0f172a',
              }}
              aria-live="polite"
            >
              {typedName.trim() || <span className="text-muted-foreground text-sm">Preview</span>}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {purpose === 'initial'
                ? "Draw your initials below. If you'd rather skip drawing, switch back to the Type tab."
                : "Draw your signature below. If you'd rather skip drawing, switch back to the Type tab."}
            </p>
            <canvas
              ref={drawCanvasRef}
              className="border rounded bg-white touch-none block w-full h-auto"
              style={{
                maxWidth: `${canvasWidth}px`,
                aspectRatio: `${canvasWidth} / ${CANVAS_HEIGHT}`,
              }}
              aria-label="Signature drawing surface"
            />
            <div>
              <Button type="button" variant="outline" size="sm" onClick={clearDraw}>
                Clear
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
